import React, { useMemo, useState } from 'react';
import { FaLink, FaMobileAlt } from 'react-icons/fa';
import { Button } from 'react-bootstrap';
import styled from '@emotion/styled';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import GenerateMobileLinkModal from '../GenerateMobileLinkModal';
import useGetPatientData from 'App/components/PatientProfile/hooks/useGetPatientData';
import PropTypes from 'prop-types';
import SendMobileLinkModal from '../SendMobileLinkModal';
import useGetPatientSubscriptionStatusQuery from 'App/hooks/useGetPatientSubscriptionStatusQuery';
import { PatientEnrollmentStates } from 'App/utils/getPatientMesssageEnrollmentStatus';
import * as R from 'ramda';

const propTypes = {
    row: PropTypes.shape({
        original: PropTypes.shape({
            patientId: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        }).isRequired,
    }).isRequired,
    value: PropTypes.string.isRequired,
};

const defaultProps = {};

const IsMobileCellStyle = styled('div')`
    display: flex;
    justify-content: center;
    align-items: center;
`;

function IsMobileCell({ row, value }) {
    const activity = row.original;

    const { patientData, isLoading } = useGetPatientData(activity.patientId);

    const isGenerateLinkEnabled = useIsPermitted(PERMISSION_TYPES.GENERATE_MOBILE_LINK);
    const isSmsSendLinkEnabled = useIsPermitted(PERMISSION_TYPES.SMS_SEND_LINK);
    const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false);
    const [showReSendMobileModal, setReSendModalModal] = useState(false);
    const status = useGetPatientSubscriptionStatusQuery(patientData?.id);

    const patientName = useMemo(() => {
        if (isLoading && !patientData) {
            return '';
        }

        const { firstName, lastName, preferredName } = patientData;

        return `${lastName}, ${firstName} ${preferredName ? `"${preferredName}"` : ''}`;
    }, [isLoading, patientData]);

    const isMobile = value === 'Yes';

    if (isMobile && (isGenerateLinkEnabled || isSmsSendLinkEnabled)) {
        return (
            <>
                <IsMobileCellStyle data-testid="is-mobile-cell">
                    {isGenerateLinkEnabled ? (
                        <Button
                            variant="link"
                            aria-label="Generate link to mobile assessment"
                            title="Generate link to mobile assessment"
                            onClick={() => setShowGenerateLinkModal(true)}
                        >
                            <FaLink />
                        </Button>
                    ) : null}
                    {R.allPass([R.always(isSmsSendLinkEnabled), R.equals(PatientEnrollmentStates.SUBSCRIBED)])(
                        status
                    ) ? (
                        <Button
                            variant="link"
                            aria-label="Send Mobile Link"
                            title="Send Mobile Link"
                            onClick={() => setReSendModalModal(true)}
                        >
                            <FaMobileAlt />
                        </Button>
                    ) : null}
                </IsMobileCellStyle>

                {showGenerateLinkModal ? (
                    <GenerateMobileLinkModal
                        onHide={() => setShowGenerateLinkModal(false)}
                        patientId={patientData.id}
                        patientName={patientName}
                        activityName={activity.name}
                        activityId={activity.id}
                    />
                ) : null}

                {showReSendMobileModal ? (
                    <SendMobileLinkModal
                        onHide={() => setReSendModalModal(false)}
                        patientId={patientData.id}
                        patientName={patientName}
                        activityName={activity.name}
                        activityId={activity.id}
                    />
                ) : null}
            </>
        );
    }
    return null;
}

IsMobileCell.propTypes = propTypes;
IsMobileCell.defaultProps = defaultProps;

export default IsMobileCell;
